import { useTranslation } from 'react-i18next'
import { Shield, Thermometer, Microscope, Truck, Users, Award } from 'lucide-react'

export function FoodSafetySection() {
  const { t } = useTranslation('common')

  const safetyPillars = [
    {
      icon: Thermometer,
      key: 'temperature',
    },
    {
      icon: Microscope,
      key: 'laboratory',
    },
    {
      icon: Truck,
      key: 'supplyChain',
    },
    {
      icon: Users,
      key: 'training',
    },
  ]

  const commitments = [
    'rawMaterials',
    'production',
    'packaging',
    'distribution',
  ]

  return (
    <section className="py-16 md:py-24 bg-[#FDF8F3]">
      <div className="container-fiorucci">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-[#5C1A1A]/10 text-[#5C1A1A] text-sm font-medium rounded-full mb-4">
            {t('nutrition.safety.badge')}
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-[#4E2118] mb-6">
            {t('nutrition.safety.title')}
          </h2>
          <p className="text-lg text-[#4E2118]/70 leading-relaxed">
            {t('nutrition.safety.subtitle')}
          </p>
        </div>

        {/* Safety Pillars Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {safetyPillars.map((pillar, index) => (
            <div
              key={pillar.key}
              className="group relative p-6 bg-white rounded-2xl border border-[#4E2118]/5 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
            >
              {/* Number badge */}
              <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#4E2118]/5 flex items-center justify-center">
                <span className="text-sm font-bold text-[#4E2118]/40">0{index + 1}</span>
              </div>

              <div className="w-14 h-14 rounded-xl bg-[#5C1A1A]/10 flex items-center justify-center mb-4 group-hover:bg-[#5C1A1A] transition-colors duration-300">
                <pillar.icon className="w-7 h-7 text-[#5C1A1A] group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="font-serif text-lg font-semibold text-[#4E2118] mb-2">
                {t(`nutrition.safety.pillars.${pillar.key}.title`)}
              </h3>
              <p className="text-sm text-[#4E2118]/70 leading-relaxed">
                {t(`nutrition.safety.pillars.${pillar.key}.description`)}
              </p>
            </div>
          ))}
        </div>

        {/* Main Content Area */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left: Description */}
          <div className="space-y-6">
            <h3 className="font-serif text-2xl font-bold text-[#4E2118]">
              {t('nutrition.safety.commitmentTitle')}
            </h3>
            <p className="text-[#4E2118]/80 leading-relaxed">
              {t('nutrition.safety.commitmentText1')}
            </p>
            <p className="text-[#4E2118]/80 leading-relaxed">
              {t('nutrition.safety.commitmentText2')}
            </p>

            {/* Commitment List */}
            <div className="pt-4">
              <ul className="space-y-3">
                {commitments.map((commitment) => (
                  <li key={commitment} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#00441C]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3.5 h-3.5 text-[#00441C]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-[#4E2118]/80">{t(`nutrition.safety.commitments.${commitment}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right: Visual - Safety Shield */}
          <div className="relative flex justify-center">
            <div className="relative w-72 h-72">
              {/* Outer rings */}
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-[#4E2118]/10 animate-spin" style={{ animationDuration: '20s' }} />
              <div className="absolute inset-4 rounded-full border border-[#DF631E]/20" />

              {/* Center shield */}
              <div className="absolute inset-8 rounded-full bg-gradient-to-br from-[#5C1A1A] to-[#3D1111] flex items-center justify-center shadow-2xl">
                <div className="text-center text-white p-6">
                  <Shield className="w-16 h-16 mx-auto mb-3 text-white/90" />
                  <span className="block text-2xl font-serif font-bold">100%</span>
                  <span className="block text-sm opacity-90">{t('nutrition.safety.shield.label')}</span>
                </div>
              </div>

              {/* Orbiting elements */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
                <Thermometer className="w-5 h-5 text-[#DF631E]" />
              </div>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
                <Microscope className="w-5 h-5 text-[#00441C]" />
              </div>
              <div className="absolute left-0 top-1/2 -translate-x-2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
                <Truck className="w-5 h-5 text-[#4E2118]" />
              </div>
              <div className="absolute right-0 top-1/2 translate-x-2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-[#5C1A1A]" />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA Box */}
        <div className="bg-white rounded-2xl p-8 md:p-10 border border-[#4E2118]/5 shadow-sm">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="font-serif text-xl font-semibold text-[#4E2118] mb-2">
                {t('nutrition.safety.cta.title')}
              </h3>
              <p className="text-[#4E2118]/70">
                {t('nutrition.safety.cta.description')}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-[#00441C]/10 rounded-full">
                <Shield className="w-5 h-5 text-[#00441C]" />
                <span className="text-sm font-medium text-[#00441C]">BRCGS</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-[#DF631E]/10 rounded-full">
                <Award className="w-5 h-5 text-[#DF631E]" />
                <span className="text-sm font-medium text-[#DF631E]">IFS</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
